import { useEffect, useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import useIsMobile from "components/hooks/useIsMobile";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import AdvisorRanking from "components/matchmaking/AdvisorRanking";
import { Family, FamilyStatus } from "protogen/advisors_service_pb";
import { useNavigate, useParams } from "react-router-dom";
import { useGetFamilyDetails } from "services/advisor";
import { Users, MapPin, Calendar } from "lucide-react";
import LinkRouter from "components/navigation/LinkRouter";
import { getFormattedLocation } from "components/family/utils";

const FamilyCard = ({ family }: { family: Family }) => {
  const isMobile = useIsMobile();

  const getStatusChip = (status: FamilyStatus) => {
    const getStatusConfig = (status: FamilyStatus) => {
      switch (status) {
        case FamilyStatus.ACTIVE:
          return { label: "Active", bgColor: "#ECF4EC", textColor: "#198282" };
        case FamilyStatus.PROSPECT:
          return {
            label: "Prospect",
            bgColor: "#EEF3FC",
            textColor: "#3061AF",
          };
        case FamilyStatus.PREACTIVATION:
          return {
            label: "Pre-activation",
            bgColor: "#FAE5D3",
            textColor: "#8E4415",
          };
        case FamilyStatus.DEACTIVATED:
          return {
            label: "Deactivated",
            bgColor: "#F5F5F5",
            textColor: "#6B6E7B",
          };
        case FamilyStatus.TEST:
          return { label: "Test", bgColor: "#FFE0E0", textColor: "#910838" };
        case FamilyStatus.DEMO:
          return { label: "Demo", bgColor: "#FFE0E0", textColor: "#910838" };
        case FamilyStatus.DEACTIVATED_PROSPECT:
          return {
            label: "Deactivated Prospect",
            bgColor: "#F5F5F5",
            textColor: "#6B6E7B",
          };
        case FamilyStatus.TEST_HIDDEN:
          return {
            label: "Test Hidden",
            bgColor: "#F5F5F5",
            textColor: "#6B6E7B",
          };
        default:
          return { label: "Unknown", bgColor: "#F5F5F5", textColor: "#6B6E7B" };
      }
    };

    const config = getStatusConfig(status);
    return (
      <Chip
        label={config.label}
        variant="filled"
        sx={{
          background: config.bgColor,
          color: config.textColor,
          height: "24px",
        }}
      />
    );
  };
  const address = getFormattedLocation(family.address).replace(", USA", "");

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        width: "100%",
        background: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "start" : "center",
          gap: "12px",
          padding: isMobile ? "12px 20px" : "16px 24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-between",
            alignItems: isMobile ? "start" : "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              overflow: "hidden",
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <LinkRouter
                to={`/families/${encodeURIComponent(family.ref)}`}
                sx={{ textDecoration: "none" }}
              >
                <Typography
                  variant={isMobile ? "h4" : "bodyHeavy"}
                  sx={{ color: "#198282" }}
                >
                  {family.name} Family
                </Typography>
              </LinkRouter>
              {getStatusChip(family.status)}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {address && (
                <Typography
                  variant="body"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <MapPin size={14} />
                  <LinkRouter
                    targetNew
                    to={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      address,
                    )}`}
                  >
                    {address}
                  </LinkRouter>
                </Typography>
              )}

              <Typography
                variant="body"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <Users size={14} />
                {family.familyMembers.length} member
                {family.familyMembers.length !== 1 ? "s" : ""}
              </Typography>

              {family.startDate && (
                <Typography
                  variant="body"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <Calendar size={14} />
                  Started {family.startDate}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const params = useParams();
  const navigate = useNavigate();
  const [selectedFamily, setSelectedFamily] = useState<string | null>(
    params.familyRef || null,
  );
  const [selectedFamilyData, setSelectedFamilyData] = useState<Family | null>(
    null,
  );
  const { request: getFamilyDetails } = useGetFamilyDetails();

  useEffect(() => {
    const getFamily = async () => {
      if (selectedFamily) {
        const response = await getFamilyDetails({
          familyRef: selectedFamily,
        });
        setSelectedFamilyData(response?.family || null);
      }
      navigate(
        `/tools/rematching/${encodeURIComponent(selectedFamily || "")}`,
        {
          replace: true,
        },
      );
    };
    getFamily();
  }, [selectedFamily]);

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              display="flex"
              flexDirection="row"
              gap="12px"
              alignContent="center"
            >
              <Typography variant="display">Rematching</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
          paddingBottom: "16px",
        }}
      >
        <AdminFamilySelect
          selectedFamily={selectedFamily}
          onChange={(familyRef) => {
            setSelectedFamily(familyRef);
          }}
          title="Select Family"
        />

        {selectedFamilyData && <FamilyCard family={selectedFamilyData} />}

        {selectedFamily && (
          <AdvisorRanking familyRef={selectedFamily} onStarted={() => {}} />
        )}
      </Box>
    </Box>
  );
};
